import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";

const MAX_POLLS = 8;

export const PaymentSuccess = () => {
  const { tr, lp } = useLang();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error
  const [ref, setRef] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let polls = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setRef(data.ref);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      polls += 1;
      if (polls >= MAX_POLLS) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {state === "checking" && (<>
        <Loader2 className="w-16 h-16 text-brand mx-auto animate-spin" />
        <p className="text-xl text-ink-muted mt-6">{tr.pay.verifying}</p>
      </>)}
      {state === "paid" && (<>
        <div className="w-20 h-20 rounded-full bg-green-100 grid place-items-center mx-auto"><CheckCircle2 className="w-12 h-12 text-green-600" /></div>
        <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-ink mt-6">{tr.pay.successTitle}</h1>
        <p className="text-xl text-ink-muted mt-4">{tr.pay.successMsg}</p>
        {ref && <div className="mt-6 inline-block bg-cream rounded-2xl px-6 py-4"><span className="text-ink-muted">{tr.pay.ref}: </span><span className="font-heading font-extrabold text-2xl text-brand">{ref}</span></div>}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {ref && <a data-testid="invoice-download" href={`${API}/bookings/${ref}/invoice.pdf`} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full h-14 px-8 border-2 font-bold text-lg"><Download className="w-5 h-5 mr-2" /> {tr.pay.invoice}</Button></a>}
          <Button onClick={() => navigate(lp("/"))} data-testid="pay-home-btn" className="rounded-full h-14 px-8 bg-brand hover:bg-brand-hover text-white font-bold text-lg">{tr.pay.backHome}</Button>
        </div>
      </>)}
      {state === "error" && (<>
        <div className="w-20 h-20 rounded-full bg-amber-100 grid place-items-center mx-auto"><Loader2 className="w-12 h-12 text-amber-600" /></div>
        <h1 className="font-heading font-bold text-2xl text-ink mt-6">{tr.pay.verifying}</h1>
        <p className="text-lg text-ink-muted mt-4">{tr.pay.cancelMsg}</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Button onClick={() => navigate(lp("/reservation"))} className="rounded-full h-14 px-8 bg-accent2 text-white font-bold text-lg">{tr.pay.retry}</Button>
          <Button onClick={() => navigate(lp("/"))} variant="outline" className="rounded-full h-14 px-8 border-2 font-bold text-lg">{tr.pay.backHome}</Button>
        </div>
      </>)}
    </div>
  );
};

export const PaymentCancel = () => {
  const { tr, lp } = useLang();
  const navigate = useNavigate();
  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <div className="w-20 h-20 rounded-full bg-red-100 grid place-items-center mx-auto"><XCircle className="w-12 h-12 text-red-600" /></div>
      <h1 className="font-heading font-bold text-3xl text-ink mt-6">{tr.pay.cancelTitle}</h1>
      <p className="text-xl text-ink-muted mt-4">{tr.pay.cancelMsg}</p>
      <div className="mt-8 flex gap-3 justify-center">
        <Button onClick={() => navigate(lp("/reservation"))} className="rounded-full h-14 px-8 bg-accent2 text-white font-bold text-lg">{tr.pay.retry}</Button>
        <Button onClick={() => navigate(lp("/"))} variant="outline" className="rounded-full h-14 px-8 border-2 font-bold text-lg">{tr.pay.backHome}</Button>
      </div>
    </div>
  );
};
